import { fmt, EM } from '../components/kuroco/fmt';
import {
  FOCUS_SLOTS,
  UNSELECTED_FOCUS,
  formatFocusValue,
  chartKeyForFocus,
  readFocusActual,
  resolveFocusSelection,
  resolveSubKgi,
} from '../utils/kpiFocusCatalog';
import {
  buildRequiredSeries,
  resolveGoalMonthKey,
  monthDiff,
  buildDriverRevenueRequired,
  buildScenarioRequired,
  scenarioSeries,
  solveDriverPlan,
  readDriverRow,
  buildRequiredInputFingerprint,
} from '../utils/goalTrajectory';
import { formatIsoDateShort, isYmComplete, jstMonth, jstToday } from '../utils/jstDate';
import { resolvePlanPeriod } from '../utils/planPeriod';
/** Skip「必要」rebuild when goals + POS + forecast fingerprint is unchanged. */
let _metricsCache = { fp: null, result: null };

/** Test / forced refresh helper. */
export function clearMetricsCache() {
  _metricsCache = { fp: null, result: null };
}

/** Scorecard key → chart metric key */
export const SC_TO_METRIC = {
  totalSales: 'revenue',
  techSales: 'techSales',
  retailSales: 'retailSales',
  customers: 'customerCount',
  newCustomers: 'newCustomerCount',
  repeatCustomers: 'repeatCustomerCount',
  unitPrice: 'customerUnitPrice',
  techUnitPrice: 'techUnitPrice',
  nomination: 'nominationRate',
  visitFreq: 'visitFrequency',
};

const SCORECARD_DEFS = [
  { key: 'totalSales', label: '総売上', unit: 'currency', field: 'total_sales' },
  { key: 'techSales', label: '技術売上', unit: 'currency', derived: (k) => deriveTechSales(k) },
  { key: 'retailSales', label: '店販売上', unit: 'currency', field: 'retail_sales' },
  { key: 'newSales', label: '新規売上', unit: 'currency', field: 'total_sales_new' },
  { key: 'repeatSales', label: 'リピート売上', unit: 'currency', field: 'total_sales_repeat' },
  { key: 'nominationSales', label: '指名売上', unit: 'currency', field: 'nominated_customer_total_sales' },
  { key: 'freeSales', label: 'フリー売上', unit: 'currency', field: 'free_customer_total_sales' },
  { key: 'customers', label: '客数', unit: 'count', field: 'customer_count' },
  { key: 'newCustomers', label: '新規客数', unit: 'count', field: 'new_customer_count' },
  { key: 'repeatCustomers', label: 'リピート客数', unit: 'count', field: 'repeat_customer_count' },
  { key: 'nominationCustomers', label: '指名客数', unit: 'count', field: 'nominated_customers' },
  { key: 'freeCustomers', label: 'フリー客数', unit: 'count', field: 'free_customers' },
  { key: 'unitPrice', label: '客単価', unit: 'currency', derived: (k) => deriveUnitPrice(k) },
  { key: 'techUnitPrice', label: '技術単価', unit: 'currency', derived: (k) => deriveTechUnitPrice(k) },
  { key: 'nomination', label: '指名率', unit: 'percent', field: 'nomination_rate' },
  {
    key: 'newReturn',
    label: '新規再来率',
    unit: 'percent',
    derived: (k) => {
      const v = k?.new_return_rate ?? k?.new_customer_return_rate;
      return v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
    },
  },
  { key: 'otherReturn', label: '新規以外再来率', unit: 'percent', field: 'existing_customer_return_rate' },
  { key: 'churnRate', label: '離脱率', unit: 'percent', field: 'churn_rate' },
  { key: 'productRate', label: '店販購入比率', unit: 'percent', field: 'retail_purchase_rate' },
  { key: 'karteCount', label: 'カルテ枚数', unit: 'pieces', field: 'karte_count' },
  {
    key: 'visitFreq',
    label: '来店周期',
    unit: 'days',
    derived: (k) => {
      const v = k?.avg_visit_interval_repeat ?? k?.visit_frequency;
      return v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
    },
  },
  { key: 'ltv', label: '年間平均LTV', unit: 'currency', field: 'avg_annual_sales_per_active_customer' },
  { key: 'loyalAnnualSales', label: '固定客年間売上', unit: 'currency', field: 'fixed_customer_annual_sales' },
];

const CHART_METRIC_DEFS = [
  { key: 'revenue', label: '売上', unit: 'currency', history: (h) => h.total_sales },
  { key: 'techSales', label: '技術売上', unit: 'currency', history: (h) => deriveTechSales(h) },
  { key: 'retailSales', label: '店販売上', unit: 'currency', history: (h) => h.retail_sales },
  { key: 'newSales', label: '新規売上', unit: 'currency', history: (h) => h.total_sales_new },
  { key: 'repeatSales', label: 'リピート売上', unit: 'currency', history: (h) => h.total_sales_repeat },
  { key: 'nominationSales', label: '指名売上', unit: 'currency', history: (h) => h.nominated_customer_total_sales },
  { key: 'nominationRate', label: '指名率', unit: 'percent', history: (h) => h.nomination_rate },
  { key: 'customerCount', label: '客数', unit: 'count', history: (h) => readDriverRow(h)?.count ?? null },
  { key: 'newCustomerCount', label: '新規客数', unit: 'count', history: (h) => h.new_customer_count },
  { key: 'repeatCustomerCount', label: 'リピート客数', unit: 'count', history: (h) => h.repeat_customer_count },
  { key: 'nominationCustomers', label: '指名客数', unit: 'count', history: (h) => h.nominated_customers },
  { key: 'customerUnitPrice', label: '客単価', unit: 'currency', history: (h) => deriveUnitPrice(h) },
  { key: 'techUnitPrice', label: '技術単価', unit: 'currency', history: (h) => deriveTechUnitPrice(h) },
  {
    key: 'visitFrequency',
    label: '来店周期',
    unit: 'days',
    history: (h) => h.avg_visit_interval_repeat ?? h.visit_frequency,
  },
  { key: 'newTechnicalUnitPrice', label: '技術単価(新規)', unit: 'currency', history: (h) => h.new_technical_unit_price },
  { key: 'repeatTechnicalUnitPrice', label: '技術単価(リピート)', unit: 'currency', history: (h) => h.repeat_technical_unit_price },
  {
    key: 'newReturn',
    label: '新規再来率',
    unit: 'percent',
    history: (h) => h.new_return_rate ?? h.new_customer_return_rate,
  },
];

/** BQ ml_forecast columns (structural v1.2+ wide surface). */
const FORECAST_FIELD_MAP = {
  revenue: 'total_sales',
  techSales: null,
  retailSales: 'retail_sales',
  nominationRate: 'nomination_rate',
  newSales: 'total_sales_new',
  repeatSales: 'total_sales_repeat',
  nominationSales: 'nominated_customer_total_sales',
  customerCount: 'customer_count',
  newCustomerCount: 'new_customer_count',
  repeatCustomerCount: 'repeat_customer_count',
  nominationCustomers: 'nominated_customers',
  customerUnitPrice: null,
  techUnitPrice: null,
  visitFrequency: 'visit_frequency',
  newTechnicalUnitPrice: 'new_technical_unit_price',
  repeatTechnicalUnitPrice: 'repeat_technical_unit_price',
  newReturn: 'new_return_rate', // API alias of new_customer_return_rate
  productRate: 'retail_purchase_rate',
};

/** Chart metric → field of the reconciled「目標達成に必要」scenario row. */
const SCENARIO_FIELD_MAP = {
  revenue: 'total_sales',
  techSales: 'tech_sales',
  retailSales: 'retail_sales',
  customerUnitPrice: 'unit_price',
  techUnitPrice: 'tech_unit_price',
  newCustomerCount: 'new_customer_count',
  repeatCustomerCount: 'repeat_customer_count',
  nominationRate: 'nomination_rate',
  newReturn: 'new_return_rate',
  customerCount: 'customer_count',
  newSales: 'new_sales',
  repeatSales: 'repeat_sales',
  nominationSales: 'nomination_sales',
  nominationCustomers: 'nominated_customers',
};

/** Map 注力 focusKey → CHART_METRIC_DEFS.key (catalog chartKey may differ, e.g. retail). */
function metricKeyForFocus(focusKey) {
  if (!focusKey) return null;
  if (focusKey === 'retailSales') return 'retailSales';
  if (focusKey === 'newReturn') return 'newReturn';
  if (focusKey === 'unitPrice') return 'customerUnitPrice';
  if (focusKey === 'nomination') return 'nominationRate';
  if (focusKey === 'totalSales') return 'revenue';
  if (focusKey === 'customerCount') return 'customerCount';
  return chartKeyForFocus(focusKey);
}

/** Resolve numeric chart target from goals (chartTargets, salesTarget, focus fields). */
function resolveChartTarget(metricKey, goals) {
  if (!goals) return null;
  const fromChart = goals.chartTargets?.[metricKey];
  if (fromChart != null && !Number.isNaN(Number(fromChart))) return Number(fromChart);

  // KGI: GoalEdit / confirm write finalSales (+ often salesTarget). Either must lock 必要.
  if (metricKey === 'revenue') {
    const kgi = goals.finalSales ?? goals.salesTarget;
    if (kgi != null && !Number.isNaN(Number(kgi))) return Number(kgi);
  }

  const selection = resolveFocusSelection(goals);
  // 注力①②③ and the 追加KGI each own the target of their chart
  for (const key of goalMetricKeys(goals)) {
    if (metricKeyForFocus(key) !== metricKey) continue;
    const t = resolveFocusTarget(key, goals);
    if (t != null) return t;
  }

  // Legacy 客単価 slot-② storage — only when 客単価 really is the selected ②
  if (metricKey === 'customerUnitPrice' && selection.subFocusKey === 'unitPrice') {
    const fromFocus = goals.focus?.find((g) => g.key === 'unitPrice')?.target;
    const v = fromFocus ?? goals.subFocusTarget ?? null;
    if (v != null && !Number.isNaN(Number(v))) return Number(v);
  }
  return null;
}

function resolveFocusTarget(focusKey, goals) {
  if (!goals || !focusKey) return null;
  const fromArr = goals.focus?.find((g) => g.key === focusKey)?.target;
  if (fromArr != null && !Number.isNaN(Number(fromArr))) return Number(fromArr);
  // 注力 has no default: an unset focusKey owns no target.
  // Each slot's target belongs to that slot's key (focusTarget ↔ focusKey, …).
  const selection = resolveFocusSelection(goals);
  const slot = FOCUS_SLOTS.find((s) => selection[s.keyField] === focusKey);
  const t = slot ? goals[slot.targetField] : null;
  if (t != null && !Number.isNaN(Number(t))) return Number(t);
  // The 追加KGI owns its own target
  const sub = resolveSubKgi(goals);
  if (sub && sub.key === focusKey) return sub.target;
  return null;
}

/** Keys that carry a goal besides 売上: 注力①②③ and the 追加KGI. They get 目標 + 必要 lines. */
function goalMetricKeys(goals) {
  const sub = resolveSubKgi(goals);
  return [...resolveFocusSelection(goals).keys, ...(sub ? [sub.key] : [])];
}

function deriveTechSales(k) {
  if (!k) return null;
  if (k.technical_sales != null && !Number.isNaN(Number(k.technical_sales))) {
    return Number(k.technical_sales);
  }
  if (k.total_sales == null || k.retail_sales == null) return null;
  return k.total_sales - k.retail_sales;
}

function deriveUnitPrice(k) {
  if (!k) return null;
  if (k.unit_price != null && !Number.isNaN(Number(k.unit_price))) return Math.round(Number(k.unit_price));
  if (k.total_sales == null) return null;
  const count = k.customer_count ?? (k.new_customer_count != null && k.repeat_customer_count != null
    ? k.new_customer_count + k.repeat_customer_count
    : null);
  if (!count) return null;
  return Math.round(k.total_sales / count);
}

function deriveTechUnitPrice(k) {
  if (!k) return null;
  if (k.technical_unit_price != null && !Number.isNaN(Number(k.technical_unit_price))) {
    return Math.round(Number(k.technical_unit_price));
  }
  const tech = deriveTechSales(k);
  if (tech == null) return null;
  const count = k.customer_count ?? (k.new_customer_count != null && k.repeat_customer_count != null
    ? k.new_customer_count + k.repeat_customer_count
    : null);
  if (!count) return null;
  return Math.round(tech / count);
}

function parseMonthKey(monthKey) {
  if (!monthKey) return null;
  const [y, m] = monthKey.split('-').map(Number);
  return { year: y, month: m };
}

function monthKeyOffset(monthKey, deltaMonths) {
  const p = parseMonthKey(monthKey);
  if (!p) return null;
  const d = new Date(p.year, p.month - 1 + deltaMonths, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function findHistoryRow(history, monthKey) {
  return history.find((h) => h.month === monthKey) || null;
}

function pctChange(current, previous, asPoints = false) {
  if (current == null || previous == null || previous === 0) return null;
  const delta = asPoints ? current - previous : ((current / previous) - 1) * 100;
  return delta;
}

function formatDelta(delta, unit, { invert = false } = {}) {
  if (delta == null || Number.isNaN(delta)) return EM;
  const effective = invert ? -delta : delta;
  const abs = Math.abs(effective);
  let tone = 'flat';
  if (effective > 0.05) tone = 'up';
  else if (effective < -0.05) tone = 'down';

  let text;
  if (unit === 'currency') {
    text = effective >= 0 ? `▲+¥${Math.round(abs).toLocaleString()}` : `▼−¥${Math.round(abs).toLocaleString()}`;
  } else if (unit === 'percent' || unit === 'points') {
    text = effective >= 0 ? `▲+${abs.toFixed(1)}pt` : `▼−${abs.toFixed(1)}pt`;
  } else if (unit === 'count') {
    text = effective >= 0 ? `▲+${Math.round(abs)}名` : `▼−${Math.round(abs)}名`;
  } else if (unit === 'days') {
    text = effective >= 0 ? `▲+${Math.round(abs)}日` : `▼−${Math.round(abs)}日`;
  } else {
    text = effective >= 0 ? `▲+${abs.toFixed(1)}%` : `▼−${abs.toFixed(1)}%`;
  }
  return { text, tone };
}

function formatPctDelta(delta) {
  if (delta == null || Number.isNaN(delta)) return { text: EM, tone: 'flat' };
  const tone = delta > 0.05 ? 'up' : delta < -0.05 ? 'down' : 'flat';
  const text = delta >= 0 ? `▲ +${delta.toFixed(1)}%` : `▼ ${delta.toFixed(1)}%`;
  return { text, tone };
}

function formatValueForUnit(value, unit) {
  if (value == null || Number.isNaN(value)) return EM;
  if (unit === 'currency') return fmt('currency', Math.round(value));
  if (unit === 'percent') return `${Number(value).toFixed(1)}%`;
  if (unit === 'count') return `${Math.round(value)}名`;
  if (unit === 'pieces') return `${Math.round(value)}件`;
  if (unit === 'days') return `${Math.round(value)}日`;
  return String(value);
}

function applyInvert(tone, invert) {
  if (!invert) return tone;
  if (tone === 'up') return 'down';
  if (tone === 'down') return 'up';
  return tone;
}

function buildRowComparisons(getValue, history, selectedMonth, unit, opts = {}) {
  const current = getValue(null);
  if (current == null || !selectedMonth) {
    return { prev: EM, year: EM, toneP: 'flat', toneY: 'flat' };
  }
  const prevKey = monthKeyOffset(selectedMonth, -1);
  const yearKey = monthKeyOffset(selectedMonth, -12);
  const prevVal = prevKey ? getValue(findHistoryRow(history, prevKey)) : null;
  const yearVal = yearKey ? getValue(findHistoryRow(history, yearKey)) : null;

  if (unit === 'percent') {
    const prevDelta = prevVal != null ? current - prevVal : null;
    const yearDelta = yearVal != null ? current - yearVal : null;
    const prevFmt = formatDelta(prevDelta, 'points', opts);
    const yearFmt = formatDelta(yearDelta, 'points', opts);
    return {
      prev: prevFmt.text,
      year: yearFmt.text,
      toneP: applyInvert(prevFmt.tone, opts.invert),
      toneY: applyInvert(yearFmt.tone, opts.invert),
    };
  }

  if (unit === 'count') {
    const prevAbs = prevVal != null ? current - prevVal : null;
    const yearPct = yearVal != null ? pctChange(current, yearVal) : null;
    const prevFmt = formatDelta(prevAbs, 'count', opts);
    const yearFmt = formatPctDelta(yearPct);
    return {
      prev: prevAbs === 0 ? '±0名' : prevFmt.text,
      year: yearFmt.text,
      toneP: applyInvert(prevFmt.tone, opts.invert),
      toneY: applyInvert(yearFmt.tone, opts.invert),
    };
  }

  if (unit === 'currency') {
    const prevAbs = prevVal != null ? current - prevVal : null;
    const yearPct = yearVal != null ? pctChange(current, yearVal) : null;
    const prevFmt = formatDelta(prevAbs, 'currency', opts);
    const yearFmt = formatPctDelta(yearPct);
    return {
      prev: prevFmt.text,
      year: yearFmt.text,
      toneP: applyInvert(prevFmt.tone, opts.invert),
      toneY: applyInvert(yearFmt.tone, opts.invert),
    };
  }

  if (unit === 'days') {
    const prevAbs = prevVal != null ? current - prevVal : null;
    const yearPct = yearVal != null ? pctChange(current, yearVal) : null;
    const prevFmt = formatDelta(prevAbs, 'days', opts);
    const yearFmt = formatPctDelta(yearPct);
    return {
      prev: prevFmt.text,
      year: yearFmt.text,
      toneP: applyInvert(prevFmt.tone, true),
      toneY: applyInvert(yearFmt.tone, true),
    };
  }

  const prevPct = prevVal != null ? pctChange(current, prevVal) : null;
  const yearPct = yearVal != null ? pctChange(current, yearVal) : null;
  const prevFmt = formatPctDelta(prevPct);
  const yearFmt = formatPctDelta(yearPct);
  return {
    prev: prevFmt.text,
    year: yearFmt.text,
    toneP: prevFmt.tone,
    toneY: yearFmt.tone,
  };
}

export function buildScorecard(kpis, history, selectedMonth) {
  // Always return every SCORECARD_DEFS card. NULL → "—" (formatValueForUnit). Keep 0 as a real value.
  return SCORECARD_DEFS.map((def) => {
    const pickField = (src) => {
      if (!src || !def.field) return null;
      let v = src[def.field];
      // karte_count alias → active_customers until older API revisions catch up
      if ((v == null || Number.isNaN(Number(v))) && def.field === 'karte_count') {
        v = src.active_customers;
      }
      return v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
    };

    let raw = null;
    if (def.field && kpis) {
      raw = pickField(kpis);
    } else if (def.derived && kpis) {
      raw = def.derived(kpis);
    }

    const getValue = (row) => {
      if (!row && kpis) {
        if (def.field) return pickField(kpis);
        if (def.derived) return def.derived(kpis);
        return null;
      }
      if (!row) return null;
      if (def.field) return pickField(row);
      if (def.derived) return def.derived(row);
      return null;
    };

    const invert = def.unit === 'days';
    const asPoints = def.unit === 'percent';
    const cmp = buildRowComparisons(getValue, history, selectedMonth, def.unit, { invert, asPoints });

    return {
      key: def.key,
      label: def.label,
      value: formatValueForUnit(raw, def.unit),
      prev: cmp.prev,
      year: cmp.year,
      toneP: cmp.toneP,
      toneY: cmp.toneY,
    };
  });
}

/**
 * Home 注力 cards: one per slot the user actually set (1–3), in slot order, each carrying its
 * `slot` number (1–3) for the ?edit=kpiN link. ②③ are optional, so an unset slot is not a card —
 * Home shows one「注力を追加」card for the free room instead of 未選択 placeholders.
 */
function buildFocusMetrics(kpis, history, selectedMonth, goals) {
  const selection = resolveFocusSelection(goals);
  const defs = [selection.focusDef, selection.subFocusDef, selection.thirdFocusDef];

  return defs.map((def, i) => ({ def, slot: i + 1 })).filter((d) => d.def).map(({ def, slot }) => {
    const current = readFocusActual(def.key, kpis, history, selectedMonth);
    const goalTarget = resolveFocusTarget(def.key, goals);
    const asPoints = def.unit === 'percent';

    const getValue = (row) => {
      if (!row) return readFocusActual(def.key, kpis, history, selectedMonth);
      // Fallback history lookup must use the comparison row's month (MoM/YoY),
      // not selectedMonth — otherwise a missing NRR on prev collapses to current.
      const rowMonth = row.month || row.ym || selectedMonth;
      return readFocusActual(def.key, row, history, rowMonth);
    };

    const cmp = buildRowComparisons(getValue, history, selectedMonth, def.unit, { asPoints });

    let gapText = EM;
    if (current != null && goalTarget != null) {
      if (def.unit === 'percent') {
        const gap = goalTarget - current;
        gapText = gap >= 0 ? `⋅${gap.toFixed(1)}pt` : `+${Math.abs(gap).toFixed(1)}pt`;
      } else if (def.unit === 'currency') {
        const gap = goalTarget - current;
        gapText = gap >= 0 ? `⋅¥${Math.round(gap).toLocaleString()}` : `+¥${Math.round(Math.abs(gap)).toLocaleString()}`;
      } else if (def.unit === 'count') {
        const gap = goalTarget - current;
        gapText = gap >= 0 ? `⋅${Math.round(gap)}名` : `+${Math.round(Math.abs(gap))}名`;
      }
    }

    const tone = cmp.toneP === 'up' ? 'up' : cmp.toneP === 'down' ? 'down' : 'flat';

    return {
      key: def.key,
      slot,
      icon: def.icon,
      label: def.label,
      value: formatValueForUnit(current, def.unit),
      target: goalTarget != null ? formatValueForUnit(goalTarget, def.unit) : EM,
      gap: gapText,
      prev: cmp.prev,
      year: cmp.year,
      tone,
      isSelected: true,
    };
  });
}

/**
 * Home KGI card row for the 追加KGI (指名率 / 指名数), or null when none is set.
 * 売上 keeps its own hero (buildSalesHero); this sits under it in the same card.
 */
function buildSubKgiHero(kpis, history, selectedMonth, goals) {
  const sub = resolveSubKgi(goals);
  if (!sub) return null;
  const actual = readFocusActual(sub.key, kpis, history, selectedMonth);
  const progress =
    actual != null && sub.target != null && sub.target > 0
      ? Math.min(999, Math.round((actual / sub.target) * 100))
      : null;
  return {
    key: sub.key,
    label: sub.def?.label || sub.key,
    unit: sub.def?.unit,
    actual,
    target: sub.target,
    actualDisplay: formatValueForUnit(actual, sub.def?.unit),
    targetDisplay: sub.target != null ? formatValueForUnit(sub.target, sub.def?.unit) : EM,
    progress,
  };
}

function buildSalesHero(kpis, history, selectedMonth, goals) {
  const actual = kpis?.total_sales ?? null;
  const target = goals?.salesTarget ?? goals?.finalSales ?? null;
  const gap = actual != null && target != null ? Math.max(0, target - actual) : null;
  const progress =
    actual != null && target != null && target > 0 ? Math.min(999, Math.round((actual / target) * 100)) : null;

  let prevCmp = EM;
  let yearCmp = EM;
  let cmpTone = 'flat';
  if (actual != null && selectedMonth) {
    const prevKey = monthKeyOffset(selectedMonth, -1);
    const yearKey = monthKeyOffset(selectedMonth, -12);
    const prevSales = findHistoryRow(history, prevKey)?.total_sales;
    const yearSales = findHistoryRow(history, yearKey)?.total_sales;
    const prevDelta = pctChange(actual, prevSales);
    const yearDelta = pctChange(actual, yearSales);
    const p = formatPctDelta(prevDelta);
    const y = formatPctDelta(yearDelta);
    prevCmp = p.text;
    yearCmp = y.text;
    cmpTone = p.tone;
  }

  return {
    progress: progress ?? EM,
    actual,
    target,
    gap,
    prev: prevCmp,
    year: yearCmp,
    cmpTone,
    progressDisplay: progress != null ? progress : EM,
  };
}

/** Home main chart: fixed past window. Future expands only when 目標期間 is 12ヶ月. */
const CHART_PAST_MONTHS = 6;
const CHART_FUTURE_DEFAULT = 6;
/** Hard cap — must cover deadline so「必要」can lock onto mokuhyo on-chart. */
const CHART_FUTURE_MAX = 18;

/**
 * Main Home chart timeline — half/half by default:
 *   last 6 months of 実績 + next 6 months of 予測 (12 ticks).
 * Future stretches to include the resolved 目標 deadline (cap 18) so the gold
 * 「必要」line can land on mokuhyo — not stop short and look like plain 予測.
 * Side cards / Goals / Settings do not use this.
 */
export function buildChartTimeline(kpiHistory, _mlForecasts, goals, now = new Date()) {
  const hist = Array.isArray(kpiHistory) ? kpiHistory : [];
  // Forecast rows do not widen the window. Extra published months stay in
  // forecastMap for series fill-in inside whatever window we build.
  void _mlForecasts;
  const actualMonths = hist
    .map((h) => h?.month)
    .filter((m) => /^\d{4}-\d{2}$/.test(m || ''))
    .sort();
  if (actualMonths.length === 0) {
    return { months: [], monthKeys: [], lastActualIdx: -1 };
  }

  // Anchor on the last month that has finished: a few days of the current month are not
  // a monthly actual. Falls back to the newest month when none has finished yet.
  const completeMonths = actualMonths.filter((m) => isYmComplete(m, now));
  const anchorMonths = completeMonths.length > 0 ? completeMonths : actualMonths;
  const lastActualMonthKey = anchorMonths[anchorMonths.length - 1];
  const { goalMonthKey } = resolveGoalMonthKey(goals, lastActualMonthKey);
  const stepsToDeadline =
    goalMonthKey != null ? monthDiff(lastActualMonthKey, goalMonthKey) : null;
  // Default 6 future; if deadline is further, stretch so deadline stays on-chart.
  let futureCount = CHART_FUTURE_DEFAULT;
  if (stepsToDeadline != null && stepsToDeadline > 0) {
    futureCount = Math.max(
      CHART_FUTURE_DEFAULT,
      Math.min(stepsToDeadline, CHART_FUTURE_MAX)
    );
  }

  const monthKeys = [];
  for (let i = CHART_PAST_MONTHS - 1; i >= 0; i -= 1) {
    const mk = monthKeyOffset(lastActualMonthKey, -i);
    if (mk) monthKeys.push(mk);
  }
  for (let i = 1; i <= futureCount; i += 1) {
    const mk = monthKeyOffset(lastActualMonthKey, i);
    if (mk) monthKeys.push(mk);
  }

  const months = monthKeys.map((mk) => {
    const p = parseMonthKey(mk);
    return p ? `${p.month}月` : mk;
  });
  const lastActualIdx = monthKeys.indexOf(lastActualMonthKey);

  return { months, monthKeys, lastActualIdx };
}

/**
 * goals.planMonths is deliberately NOT fed into「目標達成に必要」.
 *
 * Those stored monthly numbers used to win over the forecast-shaped path, which drew
 * the goal line as a dead-straight ramp and made it ignore seasonality entirely — and
 * because nothing in the app writes planMonths any more, the values were leftovers
 * from an older version that silently outlived every later goal change. The line now
 * always follows 予測's shape to the current target, so editing the goal recomputes it.
 *
 * The 目標 roadmap screen still reads planMonths for its month-by-month table; only the
 * chart path ignores it.
 */

/** Aligned forecast series for a focusKey (derive unit price / tech when needed). */
function forecastSeriesForFocus(focusKey, monthKeys, lastActualIdx, lastActualValue, forecastMap) {
  if (!focusKey || !Array.isArray(monthKeys)) return null;
  return monthKeys.map((mk, i) => {
    if (i < lastActualIdx) return null;
    if (i === lastActualIdx) return lastActualValue != null ? Number(lastActualValue) : null;
    const row = forecastMap[mk];
    if (!row) return null;
    if (focusKey === 'retailSales') {
      return row.retail_sales != null ? Number(row.retail_sales) : null;
    }
    if (focusKey === 'unitPrice') {
      const up = deriveUnitPrice(row);
      return up != null ? Number(up) : null;
    }
    if (focusKey === 'nomination') {
      return row.nomination_rate != null ? Number(row.nomination_rate) : null;
    }
    if (focusKey === 'customerCount') {
      const c = row.customer_count ?? (
        row.new_customer_count != null && row.repeat_customer_count != null
          ? Number(row.new_customer_count) + Number(row.repeat_customer_count)
          : null
      );
      return c != null && !Number.isNaN(Number(c)) ? Number(c) : null;
    }
    if (focusKey === 'totalSales') {
      return row.total_sales != null ? Number(row.total_sales) : null;
    }
    if (focusKey === 'newReturn') {
      const v = row.new_return_rate ?? row.new_customer_return_rate;
      return v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
    }
    return readDriverFocus(focusKey, row);
  });
}

/** Focus keys the driver plan pins that have no dedicated reader above. */
function readDriverFocus(focusKey, row) {
  const d = readDriverRow(row);
  if (!d) return null;
  if (focusKey === 'nominationCustomers') return d.nomCust;
  if (focusKey === 'nominationSales') return d.nomSales;
  if (focusKey === 'newSales') return row.total_sales_new != null ? Number(row.total_sales_new) : null;
  if (focusKey === 'repeatSales') return row.total_sales_repeat != null ? Number(row.total_sales_repeat) : null;
  if (focusKey === 'newCustomers') return d.newC;
  if (focusKey === 'repeatCustomers') return d.repC;
  if (focusKey === 'visitFreq') return d.interval;
  if (focusKey === 'techSales') return d.sales != null && d.retail != null ? d.sales - d.retail : null;
  return null;
}

function readFocusLastActual(focusKey, historyMap, lastActualMonthKey) {
  if (!focusKey || !lastActualMonthKey) return null;
  const row = historyMap[lastActualMonthKey];
  if (!row) return null;
  if (focusKey === 'retailSales') {
    return row.retail_sales != null ? Number(row.retail_sales) : null;
  }
  if (focusKey === 'unitPrice') return deriveUnitPrice(row);
  if (focusKey === 'customerCount') {
    const n = row.customer_count ?? (
      row.new_customer_count != null && row.repeat_customer_count != null
        ? row.new_customer_count + row.repeat_customer_count
        : null
    );
    return n != null ? Number(n) : null;
  }
  if (focusKey === 'newReturn') {
    const v = row.new_return_rate ?? row.new_customer_return_rate;
    return v != null ? Number(v) : null;
  }
  if (focusKey === 'nomination') {
    return row.nomination_rate != null ? Number(row.nomination_rate) : null;
  }
  if (focusKey === 'totalSales') {
    return row.total_sales != null ? Number(row.total_sales) : null;
  }
  return readDriverFocus(focusKey, row);
}

function buildMetrics(kpiHistory, mlForecasts, goals) {
  const hist = Array.isArray(kpiHistory) ? kpiHistory : [];
  const fc = Array.isArray(mlForecasts) ? mlForecasts : [];
  // The anchor month moves when the JST month rolls over, with no change in the inputs.
  const fp = `${buildRequiredInputFingerprint(hist, fc, goals)}\nm=${jstMonth()}`;
  if (_metricsCache.fp === fp && _metricsCache.result) {
    return _metricsCache.result;
  }

  const { months, monthKeys, lastActualIdx } = buildChartTimeline(hist, fc, goals);
  const historyMap = Object.fromEntries(hist.map((h) => [h.month, h]));
  const forecastMap = Object.fromEntries(fc.map((f) => [f.month, f]));
  const lastActualMonthKey = lastActualIdx >= 0 ? monthKeys[lastActualIdx] : null;
  const { goalMonthKey, source: deadlineSource } = resolveGoalMonthKey(goals, lastActualMonthKey);

  // 注力①②③ and the 追加KGI: their required paths are pins of one reconciled scenario for every
  // chart metric, and their charts get 目標 + 必要 of their own (売上 stays the spine).
  const selection = resolveFocusSelection(goals);
  const focusOverrides = {};
  const focusKeys = goalMetricKeys(goals);
  const goalChartKeys = focusKeys.map(metricKeyForFocus).filter(Boolean);
  focusKeys.forEach((fk) => {
    const chartKey = metricKeyForFocus(fk);
    const target =
      resolveFocusTarget(fk, goals) ?? (chartKey ? resolveChartTarget(chartKey, goals) : null);
    const last = readFocusLastActual(fk, historyMap, lastActualMonthKey);
    if (target == null || last == null) return;
    const { series } = buildRequiredSeries({
      monthKeys,
      lastActualIdx,
      lastActualValue: last,
      target,
      goalMonthKey,
      source: 'focus_required',
      forecast: forecastSeriesForFocus(fk, monthKeys, lastActualIdx, last, forecastMap),
    });
    if (series) focusOverrides[fk] = series;
  });
  // 売上 (KGI) is always a lever of the scenario, even when it is not a 注力 slot: the
  // reconciled rows must agree with the gold 売上 line, so 新規売上・リピート売上・客数 etc.
  // move with it and get a 必要 line of their own.
  if (!focusOverrides.totalSales) {
    const salesTarget = resolveChartTarget('revenue', goals);
    const salesLast = readFocusLastActual('totalSales', historyMap, lastActualMonthKey);
    if (salesTarget != null && salesLast != null) {
      const { series } = buildRequiredSeries({
        monthKeys,
        lastActualIdx,
        lastActualValue: salesLast,
        target: salesTarget,
        goalMonthKey,
        source: 'kgi_required',
        forecast: forecastSeriesForFocus('totalSales', monthKeys, lastActualIdx, salesLast, forecastMap),
      });
      if (series) focusOverrides.totalSales = series;
    }
  }

  const scenario = buildScenarioRequired({
    monthKeys,
    lastActualIdx,
    historyByMonth: historyMap,
    forecastByMonth: forecastMap,
    overrides: focusOverrides,
  });
  const scenarioDeadlineIdx = goalMonthKey ? monthKeys.indexOf(goalMonthKey) : -1;

  const metrics = {};
  const metricOrder = [];

  CHART_METRIC_DEFS.forEach((def) => {
    const actual = monthKeys.map((mk, i) => {
      if (i > lastActualIdx) return null; // the unfinished current month is not plotted as 実績
      const row = historyMap[mk];
      if (!row) return null;
      const v = def.history(row);
      return v != null && !Number.isNaN(v) ? v : null;
    });

    // Keep the chart if this KPI ever had 実績 — not only inside the 6-month window.
    // Sparse series can be null for every window month and still deserve a 予測 slot.
    const hasActualAnywhere = hist.some((h) => {
      const v = def.history(h);
      return v != null && !Number.isNaN(Number(v));
    });
    if (!hasActualAnywhere) return;

    const forecastField = FORECAST_FIELD_MAP[def.key];
    const forecast = monthKeys.map((mk, i) => {
      if (i <= lastActualIdx) return null;
      const row = forecastMap[mk];
      if (!row) return null;
      if (def.key === 'techSales') {
        const ts = row.total_sales;
        const rs = row.retail_sales;
        if (ts == null || rs == null) return null;
        return ts - rs;
      }
      if (def.key === 'customerUnitPrice') {
        return deriveUnitPrice(row);
      }
      if (def.key === 'techUnitPrice') {
        return deriveTechUnitPrice(row);
      }
      if (forecastField) {
        const v = row[forecastField];
        return v != null ? v : null;
      }
      return null;
    });

    // Last known value for this KPI: prefer the timeline's last-actual month, else
    // walk back inside the window, else the newest value in full history.
    let lastActualValue =
      lastActualIdx >= 0 && actual[lastActualIdx] != null ? actual[lastActualIdx] : null;
    if (lastActualValue == null && lastActualIdx >= 0) {
      for (let i = lastActualIdx - 1; i >= 0; i -= 1) {
        if (actual[i] != null) {
          lastActualValue = actual[i];
          break;
        }
      }
    }
    if (lastActualValue == null) {
      for (let i = hist.length - 1; i >= 0; i -= 1) {
        const v = def.history(hist[i]);
        if (v != null && !Number.isNaN(Number(v))) {
          lastActualValue = Number(v);
          break;
        }
      }
    }

    if (lastActualIdx >= 0 && lastActualValue != null) {
      // Bridge 予測 onto the last-actual tick even when that month is sparse for this KPI.
      if (forecast[lastActualIdx] == null) forecast[lastActualIdx] = lastActualValue;
    }

    const focusMk = metricKeyForFocus(selection.focusKey);
    const isSelectedFocus = goalChartKeys.includes(def.key);

    // 目標 belongs to the KGI (売上 + 追加KGI) and to the KPIs the user actually picked as 注力.
    // Without that gate a stale chartTargets entry keeps drawing a 目標 line for a KPI
    // nobody selected.
    const goalTarget =
      def.key === 'revenue' || isSelectedFocus ? resolveChartTarget(def.key, goals) : null;

    let required = null;
    let requiredMeta = { source: 'legacy' };

    const scenarioField = SCENARIO_FIELD_MAP[def.key];
    const scenarioVals =
      scenario.rows && scenarioField ? scenarioSeries(scenario.rows, scenarioField) : null;

    // Mokuhyo lock: never assign raw scenario/forecast as「必要」when a target exists.
    // Scenario may only supply the *shape*; buildRequiredSeries forces deadline = target.
    // (Structural 予測 stays untouched — only the gold line is mokuhyo-locked.)
    const mustLockMokuhyo =
      goalTarget != null &&
      lastActualValue != null &&
      (def.key === 'revenue' || isSelectedFocus);

    // TEMPORARY diagnostic:「目標達成に必要」renders as a plain straight ramp in
    // production while the same code blends locally, and every fallback is silent.
    // Unconditional for 売上 — logs which branch runs and with what inputs, whatever
    // path produces the line. Remove once the cause is known.
    if (def.key === 'revenue' && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.info('[必要:revenue] inputs', {
        goalMonthKey,
        deadlineSource,
        deadlineIdx: goalMonthKey ? monthKeys.indexOf(goalMonthKey) : null,
        lastActualIdx,
        lastActualMonth: monthKeys[lastActualIdx],
        lastActualValue,
        target: goalTarget,
        mustLockMokuhyo,
        monthKeys,
        forecast,
        forecastFutureCount: forecast.filter(
          (v, i) => i > lastActualIdx && v != null && !Number.isNaN(Number(v))
        ).length,
        scenarioVals,
        focusKeys,
      });
    }

    if (mustLockMokuhyo) {
      // Shape must come from structural 予測 (seasonality/wiggles). Scenario is only a
      // fallback when 予測 has no future points — otherwise 必要 goes straight to target.
      const forecastHasFuture = forecast.some(
        (v, i) => i > lastActualIdx && v != null && !Number.isNaN(Number(v))
      );
      const shapeSeries = forecastHasFuture ? forecast : scenarioVals || forecast;
      ({ series: required, meta: requiredMeta } = buildRequiredSeries({
        monthKeys,
        lastActualIdx,
        lastActualValue,
        target: goalTarget,
        goalMonthKey,
        source: forecastHasFuture ? 'yosoku_shaped_mokuhyo_lock' : deadlineSource,
        forecast: shapeSeries,
      }));
      if (required) {
        requiredMeta = {
          ...requiredMeta,
          appliedKeys: scenario.meta?.appliedKeys,
          derivedFrom: focusKeys,
        };
        const dIdx = requiredMeta.deadlineIdx ?? scenarioDeadlineIdx;
        if (dIdx >= 0 && required[dIdx] != null) {
          requiredMeta.deadlineValue = required[dIdx];
          requiredMeta.shortfall = goalTarget - required[dIdx];
        }
      }
      // If lock failed (e.g. no deadline), fall through to legacy revenue/focus builders below.
    }

    // (The old "scenario is informational" fallback is gone: with real data it
    // published lastActual-frozen values as 必要 — e.g. a 0% 新規再来率 line under
    // a 6.8% 予測. Under the plan rule, 必要 is either a pinned path or a
    // plan_from_revenue derivation — nothing else.)
    if (required == null && def.key === 'revenue') {
      const focusKey =
        selection.focusKey && selection.focusKey !== 'totalSales' ? selection.focusKey : null;
      const focusLast = focusKey
        ? readFocusLastActual(focusKey, historyMap, lastActualMonthKey)
        : null;
      const focusTarget = focusKey
        ? (resolveFocusTarget(focusKey, goals) ??
          (focusMk ? resolveChartTarget(focusMk, goals) : null))
        : null;
      const subKey = selection.subFocusKey;
      const subLast = subKey
        ? readFocusLastActual(subKey, historyMap, lastActualMonthKey)
        : null;
      const subTarget = subKey ? resolveFocusTarget(subKey, goals) : null;

      ({ series: required, meta: requiredMeta } = buildDriverRevenueRequired({
        monthKeys,
        lastActualIdx,
        lastSalesActual: lastActualValue,
        salesTarget: goalTarget,
        goalMonthKey,
        focusKey,
        focusLastActual: focusLast,
        focusTarget,
        forecastByMonth: forecastMap,
        historyByMonth: historyMap,
        salesForecast: forecast,
        focusForecast: focusKey
          ? forecastSeriesForFocus(focusKey, monthKeys, lastActualIdx, focusLast, forecastMap)
          : null,
        subFocusKey: subKey,
        subFocusLastActual: subLast,
        subFocusTarget: subTarget,
        subFocusForecast: subKey
          ? forecastSeriesForFocus(subKey, monthKeys, lastActualIdx, subLast, forecastMap)
          : null,
      }));
    } else if (required == null && isSelectedFocus) {
      // 注力①/② chart: forecast-shaped path to that KPI's target
      ({ series: required, meta: requiredMeta } = buildRequiredSeries({
        monthKeys,
        lastActualIdx,
        lastActualValue,
        target: goalTarget,
        goalMonthKey,
        source: deadlineSource,
        forecast,
      }));
    }
    // Every other metric: the reconciled scenario says what this KPI has to be when 売上 and
    // the 注力/追加KGI reach their targets. Shown as 必要 when it actually moves — a series
    // that only freezes the last 実績 (no identity ties it to the pinned levers) is skipped,
    // so a flat 0% 新規再来率 line never appears under a live 予測.
    if (required == null && scenarioVals && lastActualValue != null) {
      const future = scenarioVals.filter((v, i) => i > lastActualIdx && v != null);
      const tol = Math.abs(lastActualValue) * 1e-6 + 1e-9;
      const moves = future.some((v) => Math.abs(v - lastActualValue) > tol);
      if (future.length > 0 && moves) {
        // Same cut as buildRequiredSeries: nothing is drawn after the goal month.
        required = scenarioVals.map((v, i) =>
          i < lastActualIdx || (scenarioDeadlineIdx >= 0 && i > scenarioDeadlineIdx) ? null : v
        );
        requiredMeta = {
          source: 'scenario_derived',
          appliedKeys: scenario.meta?.appliedKeys,
          derivedFrom: focusKeys,
          deadlineIdx: scenarioDeadlineIdx,
        };
      }
    }

    // TEMPORARY diagnostic (pair of the inputs log above).
    if (def.key === 'revenue' && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.info('[必要:revenue] result', { meta: requiredMeta, required });
    }

    metrics[def.key] = {
      label: def.label,
      unit: def.unit,
      target: goalTarget,
      actual,
      forecast,
      required,
      requiredMeta,
      verdict: goals?.verdicts?.[def.key] || `実績データに基づく<strong>${def.label}</strong>の推移です。`,
    };
    metricOrder.push(def.key);
  });

  // One plan, not two. 売上 is pinned to the KGI above; every other metric is
  // solved from that path and the 注力 pins by the driver plan, so the charts
  // cannot contradict each other or the pins (92.6 客数 next to 指名客数 20 at
  // 指名率 50% was the old output). A metric the user picked as 注力 keeps its own
  // pinned path — the plan reads that same path as a pin, so both agree.
  const revenueRequired = metrics.revenue?.required;
  let driverPlan = null;
  if (Array.isArray(revenueRequired)) {
    // A goal metric keeps its own pinned path — the plan reads that same path as a pin.
    const isFocusMetric = (key) => goalChartKeys.includes(key);

    const plan = solveDriverPlan({
      monthKeys,
      lastActualIdx,
      revenueRequired,
      pins: focusOverrides,
      forecastByMonth: forecastMap,
      historyByMonth: historyMap,
    });
    driverPlan = plan.meta;

    Object.entries(plan.series).forEach(([key, series]) => {
      const metric = metrics[key];
      if (!metric || isFocusMetric(key)) return;
      if (series) {
        metric.required = series;
        metric.requiredMeta = {
          ...metrics.revenue.requiredMeta,
          source: 'driver_plan',
          derivedFrom: 'revenue',
          freeDrivers: plan.meta.freeDrivers,
          retailOverran: plan.meta.retailOverran,
        };
      } else if (!metric.required) {
        // The plan has no path for this KPI; keep a scenario-derived line if one exists.
        metric.requiredMeta = { source: 'unconstrained', reason: 'no goal constrains this KPI' };
      }
    });
    metrics.revenue.requiredMeta = {
      ...metrics.revenue.requiredMeta,
      feasibility: plan.meta.feasibility,
      pinsFixEverything: plan.meta.pinsFixEverything,
      freeDrivers: plan.meta.freeDrivers,
    };
  }

  const result = { metrics, metricOrder, chartMonths: months, monthKeys, lastActualIdx, driverPlan };
  _metricsCache = { fp, result };
  return result;
}

/**
 * "Data as of" label.
 *
 * `dataAsOf` is MAX(visit_date) from the API — the day the numbers actually reach.
 * The browser's today overstates it: the ETL runs each morning and covers through
 * the previous day, so a card refreshed at 10:00 on the 31st holds data to the 30th.
 * Falls back to the month's last day (closed months) or the browser day only when
 * the API did not report one.
 */
function formatDataAsOf(selectedMonth, dataAsOf = null) {
  const fromApi = formatIsoDateShort(dataAsOf);
  if (fromApi) return fromApi;

  if (!selectedMonth) return EM;
  const p = parseMonthKey(selectedMonth);
  if (!p) return EM;
  const now = new Date();
  const isCurrentMonth = now.getFullYear() === p.year && now.getMonth() + 1 === p.month;
  if (isCurrentMonth) {
    return `${now.getMonth() + 1}/${now.getDate()}`;
  }
  const lastDay = new Date(p.year, p.month, 0).getDate();
  return `${p.month}/${lastDay}`;
}

function buildAiSummary({ sales, focusMetrics, aiComment, loading, stylistName }) {
  if (loading) return { body: null, loading: true };
  if (aiComment) return { body: aiComment, loading: false, isHtml: false };

  const gapText = sales.gap != null ? fmt('currency', sales.gap) : EM;
  const focusLabel = focusMetrics.find((f) => f.label)?.label || '注力指標';
  const name = stylistName ? stylistName.replace(/\s*様$/, '') : 'あなた';

  return {
    body: `${name}さん、今月あと<strong>${gapText}</strong>。<strong>${focusLabel}</strong>を伸ばせば来月以降に効きます。`,
    loading: false,
    isHtml: true,
  };
}

/**
 * Transform DashboardContext state into KUROCO HomeScreen props.
 * Missing API fields → "—" / null (never invented numbers).
 */
export function buildKurocoHomeData({
  kpis,
  kpiHistory,
  mlForecasts,
  aiComment,
  loading,
  selectedMonth,
  stylistName,
  callName = null,
  goals = null,
  dataAsOf = null,
}) {
  // Use raw kpis (same as legacy KPISection / SalonDashboard tables). API coerces
  // missing BQ rows to 0 — do not treat all-zero as "no data" or HomeScreen diverges from legacy.
  const sales = buildSalesHero(kpis, kpiHistory, selectedMonth, goals);
  const subKgi = buildSubKgiHero(kpis, kpiHistory, selectedMonth, goals);
  const focusMetrics = buildFocusMetrics(kpis, kpiHistory, selectedMonth, goals);
  const kpisList = buildScorecard(kpis, kpiHistory, selectedMonth);
  const { metrics, metricOrder, chartMonths, driverPlan } = buildMetrics(kpiHistory, mlForecasts, goals);
  const aiSummary = buildAiSummary({
    sales,
    focusMetrics,
    aiComment,
    loading: loading?.aiComment,
    stylistName: callName || stylistName, // 名 when known, else the POS name
  });

  const primaryFocus = focusMetrics[0] || { label: '注力指標', icon: '🔁' };

  return {
    period: { dataAsOf: formatDataAsOf(selectedMonth, dataAsOf) },
    sales,
    subKgi,
    focusMetrics,
    focus: { label: primaryFocus.label, icon: primaryFocus.icon },
    kpis: kpisList,
    metrics,
    metricOrder,
    chartMonths,
    driverPlan,
    aiSummary,
    // 📋 毎日の行動プラン card lives on Home since 2026-09-30 — same rows as 目標 had
    actions: buildGoalActions(goals),
    isLoading: loading?.kpis || loading?.kpiHistory,
  };
}

/** Home chart's「目標達成に必要」for one metric and month; null when the chart has no value there. */
function requiredAt(plan, metricKey, monthKey) {
  if (!plan || !metricKey || !monthKey) return null;
  const idx = plan.monthKeys.indexOf(monthKey);
  // The 「必要」 path is anchored on the last actual month (its value there IS the actual),
  // so it is only a target for months after that anchor. The current month keeps the saved
  // target — same as Home's buildSalesHero — otherwise 達成率 is always 100%.
  if (idx < 0 || idx <= (plan.lastActualIdx ?? -1)) return null;
  const v = plan.metrics[metricKey]?.required?.[idx];
  return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
}

/** 達成率 — shared by Home and Goals; capped so a tiny target cannot print 12000%. */
export function pctOf(actual, target, cap = 999) {
  if (actual == null || target == null || !(target > 0)) return null;
  return Math.min(cap, Math.round((actual / target) * 100));
}

/**
 * 順調 / 遅れ for the viewed month. The goal is a monthly sales LEVEL, so the expected level
 * climbs linearly from the baseline (last full month before the plan) to the final goal,
 * then is prorated to the days the data covers. No baseline → no label (never a fake 順調).
 */
function buildPace({ salesActual, finalSales, period, kpiHistory }) {
  if (!period || period.isBeforePlan || salesActual == null || finalSales == null) return null;
  const baseKey = monthKeyOffset(period.startMonth, -1);
  const baseline = findHistoryRow(kpiHistory || [], baseKey)?.total_sales;
  if (baseline == null || !Number.isFinite(Number(baseline))) return null;
  const expectedMonth =
    Number(baseline) + (Number(finalSales) - Number(baseline)) * ((period.phaseIndex + 1) / period.planLength);
  // Fraction of the viewed month the data covers (past month → 1, future → 0)
  const cmp = monthDiff(period.viewMonth, period.asOf.slice(0, 7));
  let frac = 0;
  if (cmp > 0) frac = 1;
  else if (cmp === 0) {
    const p = parseMonthKey(period.viewMonth);
    frac = Number(period.asOf.slice(8, 10)) / new Date(p.year, p.month, 0).getDate();
  }
  if (frac <= 0) return null;
  const expectedMtd = expectedMonth * frac;
  if (!(expectedMtd > 0)) return null;
  const ratio = salesActual / expectedMtd;
  const label = ratio >= 0.95 ? '順調なペース' : ratio >= 0.75 ? 'やや遅れ' : '遅れ気味';
  const tone = ratio >= 0.95 ? 'good' : ratio >= 0.75 ? 'warn' : 'bad';
  return { label, tone, expectedMonth: Math.round(expectedMonth), expectedMtd: Math.round(expectedMtd), ratio };
}

function buildMonthSnap(kpis, selectedMonth, goals, plan = null, period = null, kpiHistory = null) {
  const p = parseMonthKey(selectedMonth);
  const label = p ? `${p.month}月` : EM;

  const salesActual = kpis?.total_sales ?? null;
  // The goal, once set, is shown for every month (owner 2026-10-04); only the plan period
  // (start + length) is pinned. Pace is skipped for months before the plan (buildPace).
  const salesTarget =
    requiredAt(plan, 'revenue', selectedMonth) ?? goals?.salesTarget ?? goals?.finalSales ?? null;
  const salesPct = pctOf(salesActual, salesTarget);
  const pace = buildPace({
    salesActual,
    finalSales: goals?.finalSales ?? goals?.salesTarget ?? null,
    period,
    kpiHistory,
  });

  // 注力①② follow the saved selection — null (not NRR / 客単価) when nothing is chosen
  const { focusKey, subFocusKey } = resolveFocusSelection(goals);
  const focusActual = focusKey ? readFocusActual(focusKey, kpis, null, selectedMonth) : null;
  const focusTarget = focusKey ? resolveFocusTarget(focusKey, goals) : null;

  const unitActual = subFocusKey ? readFocusActual(subFocusKey, kpis, null, selectedMonth) : null;
  const unitTarget = subFocusKey ? resolveFocusTarget(subFocusKey, goals) : null;
  const unitPct = pctOf(unitActual, unitTarget);

  const { thirdFocusKey } = resolveFocusSelection(goals);
  const thirdActual = thirdFocusKey ? readFocusActual(thirdFocusKey, kpis, null, selectedMonth) : null;
  const thirdTarget = thirdFocusKey ? resolveFocusTarget(thirdFocusKey, goals) : null;

  const sub = resolveSubKgi(goals);
  const subKgiActual = sub ? readFocusActual(sub.key, kpis, null, selectedMonth) : null;

  return {
    label,
    salesActual,
    salesTarget,
    salesPct,
    paceLabel: pace?.label ?? null,
    paceTone: pace?.tone ?? null,
    paceExpectedMtd: pace?.expectedMtd ?? null,
    focusActual,
    focusTarget,
    unitActual,
    unitTarget,
    unitPct,
    thirdActual,
    thirdTarget,
    subKgiActual,
    subKgiTarget: sub?.target ?? null,
  };
}

function buildGoalTrack(kpis, selectedMonth, goals, plan = null, period = null, kpiHistory = null) {
  const { focusKey, subFocusKey, thirdFocusKey } = resolveFocusSelection(goals);
  const sub = resolveSubKgi(goals);
  // The plan window is the user's own setting (start month + length) — it does not move with
  // the month picker. Without any goal, show a 6-month window from the viewed month as before.
  const per = period ?? resolvePlanPeriod({ planStartMonth: selectedMonth }, { viewMonth: selectedMonth });
  const PLAN_LENGTH = per.planLength;
  const finalSales = goals?.finalSales ?? null;
  const { goalDate, goalDateLabel } = per;

  const months = per.monthKeys.map((mk, i) => {
    const p = parseMonthKey(mk);
    const label = p ? `${p.month}月` : EM;

    const planEntry = goals?.planMonths?.find((m) => m.monthKey === mk);
    const isFinalMonth = i === PLAN_LENGTH - 1;
    // Same number as Home's「目標達成に必要」for this month; the old rule only where the chart has none
    const need = (focusKeyOrNull) =>
      focusKeyOrNull ? requiredAt(plan, metricKeyForFocus(focusKeyOrNull), mk) : null;
    const salesTarget =
      requiredAt(plan, 'revenue', mk) ??
      planEntry?.salesTarget ??
      (isFinalMonth ? finalSales : goals?.salesTarget ?? null);
    const focusTarget =
      need(focusKey) ?? planEntry?.focusTarget ?? (isFinalMonth ? goals?.focusTarget : null) ?? null;
    const unitTarget =
      need(subFocusKey) ?? planEntry?.unitTarget ?? (isFinalMonth ? goals?.subFocusTarget : null) ?? null;
    const thirdTarget =
      need(thirdFocusKey) ?? (thirdFocusKey && isFinalMonth ? goals?.thirdFocusTarget ?? null : null);
    const subKgiTarget = need(sub?.key) ?? (sub && isFinalMonth ? sub.target : null);

    const isCurrent = mk === selectedMonth;
    const isPast = monthDiff(mk, selectedMonth) > 0;
    const status = isCurrent ? 'current' : isFinalMonth ? 'final' : isPast ? 'past' : 'upcoming';
    const state = isCurrent ? '進行中' : isPast ? '終了' : isFinalMonth ? '最終' : '予定';

    let salesActual = null;
    let focusActual = null;
    let unitActual = null;
    let thirdActual = null;
    let subKgiActual = null;
    if (isCurrent && kpis) {
      salesActual = kpis.total_sales ?? null;
      // Roadmap 注力 columns follow the saved selection (no NRR / 客単価 default)
      focusActual = focusKey ? readFocusActual(focusKey, kpis, null, selectedMonth) : null;
      unitActual = subFocusKey ? readFocusActual(subFocusKey, kpis, null, selectedMonth) : null;
      thirdActual = thirdFocusKey ? readFocusActual(thirdFocusKey, kpis, null, selectedMonth) : null;
      subKgiActual = sub ? readFocusActual(sub.key, kpis, null, selectedMonth) : null;
    } else if (isPast) {
      // Months of the plan that are already over: their 実績 comes from history
      const row = findHistoryRow(kpiHistory || [], mk);
      if (row) {
        salesActual = row.total_sales ?? null;
        focusActual = focusKey ? readFocusActual(focusKey, row, kpiHistory, mk) : null;
        unitActual = subFocusKey ? readFocusActual(subFocusKey, row, kpiHistory, mk) : null;
        thirdActual = thirdFocusKey ? readFocusActual(thirdFocusKey, row, kpiHistory, mk) : null;
        subKgiActual = sub ? readFocusActual(sub.key, row, kpiHistory, mk) : null;
      }
    }

    return {
      label,
      monthKey: mk,
      status,
      state,
      salesTarget,
      salesActual,
      focusTarget,
      focusActual,
      unitTarget,
      unitActual,
      thirdTarget,
      thirdActual,
      subKgiTarget,
      subKgiActual,
    };
  });

  return {
    // Month before the plan started → 「開始前」 instead of 「n / N ヶ月目」
    phaseLabel: per.isBeforePlan ? '開始前' : per.phaseLabel,
    planLength: PLAN_LENGTH,
    finalSales,
    goalDate,
    goalDateLabel,
    startMonth: per.startMonth,
    currentIndex: per.phaseIndex,
    elapsedPct: per.elapsedPct,
    daysToGoal: per.daysToGoal,
    isBeforePlan: per.isBeforePlan,
    isAfterPlan: per.isAfterPlan,
    months,
  };
}

function formatActionCount(a) {
  if (a?.doneCount != null && Number.isFinite(Number(a.doneCount))) {
    return `今月 ${Number(a.doneCount)}回`;
  }
  const r = a?.rate;
  if (r == null || r === '') return EM;
  // Legacy "%" rates → prefer count wording when string already looks like 今月 N回
  if (typeof r === 'string' && /回/.test(r)) return r;
  if (typeof r === 'number') return `今月 ${r}回`;
  // Old "80%" style — do not show as achievement %; treat unknown as empty
  if (typeof r === 'string' && /%$/.test(r.trim())) return EM;
  return String(r);
}

function buildGoalActions(goals) {
  const actions = goals?.actions;
  if (!Array.isArray(actions) || actions.length === 0) return [];
  return actions.map((a, idx) => ({
    id: a.id ?? idx + 1,
    name: a.name ?? EM,
    cadence: a.cadence ?? EM,
    doneCount: a.doneCount != null ? Number(a.doneCount) : null,
    rate: formatActionCount(a),
    tone: a.tone ?? 'mid',
    // From AI ACTIONS / ACTION_REPLACE — shown on Goals tap-expand
    when: (a.when || '').trim() || null,
    what: (a.what || '').trim() || null,
    howMuch: (a.howMuch || '').trim() || null,
    // 指標 the action is for — ActionPlanCard groups by it when the whole plan is tagged
    focusKey: a.focusKey || null,
  }));
}

/**
 * Transform DashboardContext state into KUROCO GoalsScreen props.
 * Goal plan / targets / actions → null or "—" until goals API exists.
 */
export function buildKurocoGoalsData({
  kpis,
  selectedMonth,
  goals = null,
  loading,
  changeLog = null,
  dataAsOf = null,
  kpiHistory = null,
  mlForecasts = null,
}) {
  // JST calendar day — the salons' day, not the browser's UTC day
  const today = jstToday();
  const asOf =
    typeof dataAsOf === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dataAsOf) ? dataAsOf.slice(0, 10) : today;
  // One plan period for every screen: start month + length from the user's settings
  const hasGoal = (goals?.finalSales ?? goals?.salesTarget ?? null) != null;
  const period = hasGoal ? resolvePlanPeriod(goals, { viewMonth: selectedMonth, today, asOf }) : null;
  // Monthly targets come from Home's chart plan when history is given; callers without it keep the old targets
  const plan =
    Array.isArray(kpiHistory) && kpiHistory.length > 0 ? buildMetrics(kpiHistory, mlForecasts, goals) : null;
  const monthSnap = buildMonthSnap(kpis, selectedMonth, goals, plan, period, kpiHistory);
  const goalTrack = buildGoalTrack(kpis, selectedMonth, goals, plan, period, kpiHistory);
  const actions = buildGoalActions(goals);
  // 注力 cards'「Nヶ月後」keep the saved final targets, not the chart plan
  const baseTrack = plan ? buildGoalTrack(kpis, selectedMonth, goals, null, period, kpiHistory) : goalTrack;
  const lastMonth = baseTrack.months[baseTrack.months.length - 1];

  // 注力①②③ renkei — labels/icons come from the saved keys; 未選択 when a slot is empty
  const selection = resolveFocusSelection(goals);
  const { focusDef, subFocusDef } = selection;
  const focusTarget = focusDef
    ? goals?.focusTarget ?? goals?.focus?.find((g) => g.key === focusDef.key)?.target ?? null
    : null;
  const subFocusTarget = subFocusDef
    ? goals?.subFocusTarget ?? goals?.focus?.find((g) => g.key === subFocusDef.key)?.target ?? null
    : null;
  const slotView = (def, target, slot) => ({
    slot,
    icon: (def || UNSELECTED_FOCUS).icon,
    label: (def || UNSELECTED_FOCUS).label,
    unit: def?.unit ?? null,
    target: (def && formatFocusValue(def.unit, target)) ?? EM,
    key: def?.key ?? null,
    isSelected: Boolean(def),
  });
  const focus = slotView(focusDef, focusTarget, 1);
  const subFocus = slotView(subFocusDef, subFocusTarget, 2);
  const thirdFocus = slotView(selection.thirdFocusDef, goals?.thirdFocusTarget ?? null, 3);
  const sub = resolveSubKgi(goals);

  return {
    period: { today, dataAsOf: formatDataAsOf(selectedMonth, dataAsOf) },
    // null = no goal saved yet; isBeforePlan = the viewed month is earlier than the goal was set
    planPeriod: period,
    monthSnap,
    goalTrack,
    focus,
    subFocus,
    thirdFocus,
    // Slots that are set, in order — screens list these instead of a fixed ①② pair
    focusSlots: [focus, subFocus, thirdFocus].filter((f) => f.isSelected),
    subKgi: sub
      ? {
          key: sub.key,
          icon: sub.def?.icon,
          label: sub.def?.label || sub.key,
          unit: sub.def?.unit ?? null,
          target: formatFocusValue(sub.def?.unit, sub.target) ?? EM,
        }
      : null,
    actions,
    finalFocusTarget: lastMonth?.focusTarget ?? focusTarget ?? null,
    finalUnitTarget: lastMonth?.unitTarget ?? subFocusTarget ?? null,
    finalThirdTarget: lastMonth?.thirdTarget ?? null,
    isLoading: loading?.kpis,
    changeLog: changeLog ?? (Array.isArray(goals?.changeLog) ? goals.changeLog : []),
  };
}

/**
 * Settings stack adapter — profile + goals summary + scorecards for goal edit.
 */
export function buildKurocoSettingsData({
  kpis,
  kpiHistory,
  selectedMonth,
  loading,
  stylistName,
  selectedStore,
  stores,
  goals = null,
  profileLocal = null,
  changeLog = null,
}) {
  const goalsData = buildKurocoGoalsData({ kpis, selectedMonth, goals, loading, changeLog });
  const scoreCards = buildScorecard(kpis, kpiHistory, selectedMonth);
  const storeList = Array.isArray(stores) ? stores.map((s) => s.name || s).filter(Boolean) : [];

  const name = profileLocal?.name || stylistName || EM;
  const salon = profileLocal?.salon || selectedStore || EM;

  return {
    ...goalsData,
    scoreCards,
    stylist: {
      name,
      salon,
      rank: profileLocal?.rank || EM,
      debutYear: profileLocal?.debutY ?? null,
      debutMonth: profileLocal?.debutM ?? null,
    },
    stores: storeList.length > 0 ? storeList : salon !== EM ? [salon] : [],
  };
}

export default buildKurocoHomeData;
